import { Assistant } from "../../contexts/AssistantContext";
import { AssistantActiveState, AssistantStatus, AssistantTableData, AppearanceState, AssistantFormValues, OpenAIConfig, PineconeConfig, AssistantData } from "./types";
import { DEFAULT_APPEARANCE_STATE } from './constants';
import type { ColorPickerProps } from 'antd/es/color-picker';

// Import icons
import stripeIcon from '../../assets/icons/stripe.png';
import zendeskIcon from '../../assets/icons/zendesk.png';
import slackIcon from '../../assets/icons/slack.png';
import salesforceIcon from '../../assets/icons/salesforce.png';
import jiraIcon from '../../assets/icons/jira.png';
import pdfIcon from '../../assets/icons/pdf.png';
import gmailIcon from '../../assets/icons/gmail.svg';
import hubspotIcon from '../../assets/icons/hubspot.png';
import excelIcon from '../../assets/icons/excel.svg';
import sheetsIcon from '../../assets/icons/sheets.svg';
import openaiIcon from '../../assets/icons/openai.svg';
import n8nIcon from '../../assets/icons/n8n.webp';
import outlookIcon from '../../assets/icons/outlook.svg';

import { createLogger } from '../../utils/logger';
const logger = createLogger('features-assistants');

const iconMap: { [key: string]: string } = {
  'stripe': stripeIcon,
  'zendesk': zendeskIcon,
  'slack': slackIcon,
  'salesforce': salesforceIcon,
  'jira': jiraIcon,
  'pdf': pdfIcon,
  'gmail': gmailIcon,
  'hubspot': hubspotIcon,
  'excell': excelIcon,
  'sheets': sheetsIcon,
  'openai': openaiIcon,
  'n8n': n8nIcon,
  'outlook': outlookIcon,
};

export const toColorString = (color: ColorPickerProps['value']): string => {
  if (typeof color === 'string') {
    return color;
  }
  if (color && 'toHexString' in color && typeof color.toHexString === 'function') {
    return color.toHexString();
  }
  return '#000000';
};

export const formatTableData = (assistants: Assistant[]): AssistantTableData[] => assistants.map((bot) => ({
    key: bot.id,
    id: bot.id,
    name: bot.name,
    status: AssistantStatus.ACTIVE, // Valor por defecto
    activeState: AssistantActiveState.FOLLOWING_SCHEDULE, // Valor por defecto
    schedule: [
      { active: true, time: '09:00-17:00 (Mon–Fri)' },
      { active: false, time: '09:00-17:00 (Mon–Fri)' },
    ],
    lastSync: bot.createdAt ? new Date(bot.createdAt * 1000).toLocaleDateString() : '-',
  })).sort((a, b) => {
    // mismo status → comparar por nombre
    if (a.status === b.status) {
      return a.name.localeCompare(b.name);
    }
    // Active siempre antes que Inactive
    return a.status === 'Active' ? -1 : 1;
  });

export const getAppearanceStateFromAssistant = (assistant: any): AppearanceState => {
    if (!assistant?.webchatConfig) {
        return DEFAULT_APPEARANCE_STATE;
    }

    const { webchatConfig } = assistant;
    
    return {
        primaryColor: webchatConfig.colorTitle || DEFAULT_APPEARANCE_STATE.primaryColor,
        headerColor: webchatConfig.colorMain || DEFAULT_APPEARANCE_STATE.headerColor,
        greetingMessage: webchatConfig.greeting || DEFAULT_APPEARANCE_STATE.greetingMessage,
        iconColor: webchatConfig.iconBgColor || DEFAULT_APPEARANCE_STATE.iconColor,
    };
};

export const getIntegrationCode = (domain: string, assistantId?: string): string => {
  return `<script src="https://${domain}/lib/init.js" data-chatbot-id="${assistantId}"></script>`;
};

export const handleCopy = (code: string): void => {
  navigator.clipboard.writeText(code);
};

const getMaxTokensByLength = (length: string): number => {
  switch (length) {
    case 'Concise':
      return 300;
    case 'Standard':
      return 450;
    case 'Extended':
      return 600;
    default:
      return 450;
  }
};

export const getResponseLength = (maxTokens?: number): string => {
  if (!maxTokens) return '';
  if (maxTokens === 300) return 'Concise';
  if (maxTokens === 450) return 'Standard';
  if (maxTokens >= 600) return 'Extended';
  return '';
};

export const transformAIResponseData = (aiResponse: string) => {
  const parsedData = JSON.parse(aiResponse);

  const communicationStyleMap: Record<string, string> = {
    professional: 'professional',
    neutral: 'neutral',
    'matter-of-fact': 'matter-of-fact',
    'matter of fact': 'matter-of-fact',
    friendly: 'friendly',
    humorous: 'humorous',
  };

  return {
    agentName: parsedData.agent_name,
    businessName: parsedData.business_name,
    communicationStyle:
      communicationStyleMap[parsedData.communication_style.toLowerCase()] || 'neutral',
    responseLength: parsedData.answers_length,
    voiceLanguage: parsedData.voice_language || 'off',
    chatTopics: Array.isArray(parsedData.chat_topics)
      ? parsedData.chat_topics.join(', ')
      : parsedData.chat_topics || '',
    domainTopics: Array.isArray(parsedData.domain_topics)
      ? parsedData.domain_topics.join(' ')
      : parsedData.domain_topics,
    role: parsedData.role,
    fallbackResponse: Array.isArray(parsedData.fallback_responses)
      ? parsedData.fallback_responses.join('. ')
      : parsedData.fallback_responses,
  };
};

// Flow and Message Processing Utilities
export const getIntegrationId = (message: string | any): string => {
  // Handle case where message might be an object with a message property
  const messageStr = typeof message === 'string' ? message : message?.message || '';
  
  if (!messageStr || typeof messageStr !== 'string') {
    logger.warn('getIntegrationId: Invalid message format, expected string', message);
    return '';
  }
  
  const match = messageStr.match(/@([^/]+)\//);
  return match ? match[1] : '';
};

export const getFunctionId = (message: string | any): string => {
  // Handle case where message might be an object with a message property
  const messageStr = typeof message === 'string' ? message : message?.message || '';
  
  if (!messageStr || typeof messageStr !== 'string') {
    logger.warn('getFunctionId: Invalid message format, expected string', message);
    return '';
  }
  
  const match = messageStr.match(/@[^/]+\/([^{]+)/);
  return match ? match[1] : '';
};

export const getFunctionName = (message: string | any): string => {
  const functionId = getFunctionId(message);
  if (!functionId) return '';
  
  return functionId
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const extractActionInfo = (message: string): { parameters: any; authId: string; name: string } | null => {
  try {
    const match = message.match(/\{.*\}/);
    if (match) {
      const info = JSON.parse(match[0]);
      return {
        parameters: info.parameters || {},
        authId: info.authId,
        name: info.name,
      };
    }
  } catch (error) {
    logger.error('Error parsing action info:', error);
  }
  return null;
};

export const formatConversationMessages = (messages: Array<{ text: string; role: string }>) => {
  return messages.map((msg, index) => ({
    step: index + 1,
    message: msg.text,
    role: msg.role === 'User' ? 'user' : 'agent',
  }));
};

export const processAIFlowResponse = (data: any, currentFlowName: string, currentDescription: string) => {
  if (!data.response) {
    throw new Error('Invalid response format');
  }

  const newFlowName = data.response.flowName || currentFlowName;
  const newDescription = data.response.flowDescription || currentDescription;
  
  let newMessages = [];
  if (data.response.conversationFlow) {
    newMessages = data.response.conversationFlow.map((item: any, index: number) => ({
      id: Date.now() + index,
      text: item.M.message,
      role: item.M.role === 'user' ? 'User' : 'Virtual Agent',
      step: item.M.step || (index + 1).toString()
    }));

    newMessages.sort((a, b) => parseInt(a.step) - parseInt(b.step));
  }

  return {
    flowName: newFlowName,
    description: newDescription,
    messages: newMessages
  };
};

export const getIconForIntegration = (integration: string): string => {
  const normalizedIntegration = integration.toLowerCase();
  return iconMap[normalizedIntegration] || gmailIcon; // Fallback to gmail icon if not found
};

export const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
};

export const calculateDuration = (startedAt: string, completedAt?: string) => {
  if (!completedAt) return '-';
  const start = new Date(startedAt);
  const end = new Date(completedAt);
  const durationMs = end.getTime() - start.getTime();
  const durationSec = Math.round(durationMs / 1000);
  return `${durationSec}s`;
};
